import React, { useState } from 'react';
import {
  LayoutDashboard, PackageSearch, HeartPulse, LogOut,
  TreePine, Menu, X, ExternalLink, HardDrive, BarChart3, Trash2, Database, Layers, Zap, KeyRound,
} from 'lucide-react';

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard, path: '/admin' },
  { id: 'migration', label: 'Migration', icon: PackageSearch, path: '/admin/migration' },
  { id: 'storage', label: 'Storage', icon: HardDrive, path: '/admin/storage' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
  { id: 'cleanup', label: 'Cleanup', icon: Trash2, path: '/admin/cleanup' },
  { id: 'render-diagnostics', label: 'Render Outputs', icon: Zap, path: '/admin/render-diagnostics' },
  { id: 'carousels', label: 'Carousels', icon: Layers, path: '/admin/carousels' },
  { id: 'schema', label: 'Schema', icon: Database, path: '/admin/schema' },
  { id: 'health', label: 'Health', icon: HeartPulse, path: '/admin/health' },
  { id: 'auth-binding', label: 'Auth Binding', icon: KeyRound, path: '/admin/auth-binding' },
];

export default function AdminLayout({ activePage, children, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigate = (path) => {
    window.location.href = path;
  };

  return (
    <div style={styles.root}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div style={styles.mobileOverlay} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside style={{
        ...styles.sidebar,
        transform: sidebarOpen ? 'translateX(0)' : undefined,
      }}>
        {/* Logo */}
        <div style={styles.logoArea}>
          <div style={styles.logoIcon}>
            <TreePine size={16} color="#4ade80" />
          </div>
          <div>
            <div style={styles.logoTitle}>Magic Forest</div>
            <div style={styles.logoSub}>ADMIN</div>
          </div>
          <button style={styles.closeMobile} onClick={() => setSidebarOpen(false)}>
            <X size={18} color="#525252" />
          </button>
        </div>

        {/* Nav */}
        <nav style={styles.nav}>
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = activePage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                style={{
                  ...styles.navItem,
                  ...(active ? styles.navItemActive : {}),
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#e5e5e5'; }}}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#525252'; }}}
              >
                <Icon size={16} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Bottom */}
        <div style={styles.sidebarBottom}>
          <a href="/" style={styles.viewSiteLink}>
            <ExternalLink size={14} />
            <span>View App</span>
          </a>
          <div style={styles.userRow}>
            <span style={{ fontSize: 11, color: '#3a3a3a' }}>admin</span>
            {onLogout && (
              <button onClick={onLogout} style={styles.logoutBtn} title="Sign out">
                <LogOut size={14} color="#525252" />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div style={styles.main}>
        {/* Top bar */}
        <header style={styles.topBar}>
          <button style={styles.hamburger} onClick={() => setSidebarOpen(true)}>
            <Menu size={18} color="#737373" />
          </button>
          <h1 style={styles.pageTitle}>
            {NAV_ITEMS.find(n => n.id === activePage)?.label || 'Dashboard'}
          </h1>
        </header>

        {/* Content */}
        <main style={styles.content}>
          {children}
        </main>
      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100vh',
    backgroundColor: '#0a0a0a',
    display: 'flex',
  },
  // Sidebar
  sidebar: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: 220,
    backgroundColor: '#0d0d0d',
    borderRight: '1px solid #1a1a1a',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 40,
  },
  mobileOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    zIndex: 30,
  },
  logoArea: {
    height: 56,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '0 18px',
    borderBottom: '1px solid #1a1a1a',
    flexShrink: 0,
  },
  logoIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: 'rgba(74,222,128,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#fafafa',
    letterSpacing: '-0.01em',
  },
  logoSub: {
    fontSize: 9,
    color: '#3a3a3a',
    letterSpacing: '0.12em',
    fontWeight: 600,
  },
  closeMobile: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'none', // shown via media query in real app
  },
  nav: {
    flex: 1,
    padding: '14px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 12px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    color: '#525252',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transition: 'all 0.12s',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  },
  navItemActive: {
    backgroundColor: 'rgba(74,222,128,0.08)',
    color: '#4ade80',
  },
  sidebarBottom: {
    padding: '10px 10px',
    borderTop: '1px solid #1a1a1a',
  },
  viewSiteLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 12,
    color: '#3a3a3a',
    textDecoration: 'none',
    transition: 'color 0.12s',
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
  },
  logoutBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    borderRadius: 4,
  },
  // Main content area
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    marginLeft: 220,
  },
  topBar: {
    height: 56,
    display: 'flex',
    alignItems: 'center',
    padding: '0 28px',
    borderBottom: '1px solid #1a1a1a',
    backgroundColor: '#0a0a0a',
    position: 'sticky',
    top: 0,
    zIndex: 20,
    flexShrink: 0,
  },
  hamburger: {
    display: 'none', // shown via media query
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    marginRight: 12,
  },
  pageTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#fafafa',
    margin: 0,
    letterSpacing: '-0.01em',
  },
  content: {
    flex: 1,
    padding: '28px 32px',
    overflowY: 'auto',
  },
};
